import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';

// معادلة الإنتاج — production runs built from item recipes. Completing
// a run consumes the raw materials (incl. wastage) and produces the
// finished stock, updating the item's cost from the real batch cost.

interface ProductionLine {
  ingredientNameSnapshot: string;
  totalQuantity: number;
  unitCostSnapshot: number;
  lineCost: number;
}

interface ProductionOrder {
  productionOrderId: string;
  number: string;
  itemNameSnapshot: string;
  quantity: number;
  wastagePercent: number;
  status: number;
  totalCost: number;
  unitCost: number;
  completedAt: string | null;
  lines: ProductionLine[];
}

interface ItemDto {
  itemId: string;
  nameAr: string;
}

interface BranchDto {
  branchId: string;
  nameAr: string;
}

export function ProductionPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState<ProductionOrder | null>(null);

  const { data: orders, isLoading } = useQuery({
    queryKey: ['production'] as const,
    queryFn: async () => (await apiClient.get<ProductionOrder[]>('/api/production')).data,
  });

  const complete = useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post<ProductionOrder>(`/api/production/${id}/complete`)).data,
    onSuccess: (order) => {
      void queryClient.invalidateQueries({ queryKey: ['production'] });
      notifications.show({
        color: 'green',
        title: t('production.completed'),
        message: `${order.number} — ${t('production.unitCost')}: ${order.unitCost}`,
      });
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        title: t('production.failed'),
        message: axios.isAxiosError(err) ? ((err.response?.data as { title?: string })?.title ?? '') : '',
      }),
  });

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.operations')}
        title={t('production.title')}
        subtitle={t('production.subtitle')}
      />

      <Group justify="flex-end">
        <Button
          color="wheels"
          leftSection={<IconPlus size={16} stroke={1.8} />}
          onClick={() => setCreateOpen(true)}
        >
          {t('production.new')}
        </Button>
      </Group>

      <Paper p="md">
        {isLoading ? (
          <Group justify="center" py="lg">
            <Loader color="wheelsRed" />
          </Group>
        ) : (orders ?? []).length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="lg">
            {t('production.empty')}
          </Text>
        ) : (
          <Table verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('production.number')}</Table.Th>
                <Table.Th>{t('production.item')}</Table.Th>
                <Table.Th>{t('production.qty')}</Table.Th>
                <Table.Th>{t('production.wastage')}</Table.Th>
                <Table.Th>{t('production.totalCost')}</Table.Th>
                <Table.Th>{t('production.statusCol')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(orders ?? []).map((order) => (
                <Table.Tr key={order.productionOrderId}>
                  <Table.Td dir="ltr" className="font-mono">{order.number}</Table.Td>
                  <Table.Td>{order.itemNameSnapshot}</Table.Td>
                  <Table.Td className="font-numeric">{order.quantity}</Table.Td>
                  <Table.Td className="font-numeric">{order.wastagePercent}%</Table.Td>
                  <Table.Td className="font-numeric">{order.totalCost.toFixed(2)} ₪</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={order.status === 2 ? 'green' : 'yellow'}>
                      {order.status === 2 ? t('production.done') : t('production.draft')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      <Button size="xs" variant="default" onClick={() => setDetail(order)}>
                        {t('production.plan')}
                      </Button>
                      {order.status === 1 && (
                        <Button
                          size="xs"
                          color="wheels"
                          loading={complete.isPending}
                          onClick={() => complete.mutate(order.productionOrderId)}
                        >
                          {t('production.complete')}
                        </Button>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <CreateModal opened={createOpen} onClose={() => setCreateOpen(false)} />

      <Modal
        opened={detail !== null}
        onClose={() => setDetail(null)}
        title={`${detail?.number ?? ''} — ${detail?.itemNameSnapshot ?? ''}`}
      >
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('production.ingredient')}</Table.Th>
              <Table.Th>{t('production.consumed')}</Table.Th>
              <Table.Th>{t('production.cost')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(detail?.lines ?? []).map((line) => (
              <Table.Tr key={line.ingredientNameSnapshot}>
                <Table.Td>{line.ingredientNameSnapshot}</Table.Td>
                <Table.Td className="font-numeric">{line.totalQuantity}</Table.Td>
                <Table.Td className="font-numeric">{line.lineCost.toFixed(2)} ₪</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Group justify="space-between" mt="sm">
          <Text size="sm" c="dimmed">{t('production.unitCost')}</Text>
          <Text fw={600} className="font-numeric">{detail?.unitCost.toFixed(4)} ₪</Text>
        </Group>
      </Modal>
    </Stack>
  );
}

function CreateModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [itemId, setItemId] = useState<string | null>(null);
  const [branchId, setBranchId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [wastage, setWastage] = useState<number>(0);

  const { data: items } = useQuery({
    queryKey: ['items-for-production'] as const,
    queryFn: async () => (await apiClient.get<ItemDto[]>('/api/items')).data,
    enabled: opened,
  });
  const { data: branches } = useQuery({
    queryKey: ['branches'] as const,
    queryFn: async () => (await apiClient.get<BranchDto[]>('/api/branches')).data,
    enabled: opened,
  });

  const mutation = useMutation({
    mutationFn: async () =>
      apiClient.post('/api/production', {
        branchId,
        itemId,
        quantity,
        wastagePercent: wastage,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['production'] });
      onClose();
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        title: t('production.failed'),
        message: axios.isAxiosError(err) ? ((err.response?.data as { title?: string })?.title ?? '') : '',
      }),
  });

  return (
    <Modal opened={opened} onClose={onClose} title={t('production.new')}>
      <Stack gap="sm">
        <Select
          label={t('production.item')}
          description={t('production.itemHint')}
          value={itemId}
          onChange={setItemId}
          data={(items ?? []).map((i) => ({ value: i.itemId, label: i.nameAr }))}
          searchable
          required
        />
        <Select
          label={t('production.branch')}
          value={branchId}
          onChange={setBranchId}
          data={(branches ?? []).map((b) => ({ value: b.branchId, label: b.nameAr }))}
          required
        />
        <Group grow>
          <NumberInput
            label={t('production.qty')}
            value={quantity}
            onChange={(v) => setQuantity(typeof v === 'number' ? v : 1)}
            min={0.0001}
            decimalScale={4}
          />
          <NumberInput
            label={t('production.wastage')}
            value={wastage}
            onChange={(v) => setWastage(typeof v === 'number' ? v : 0)}
            min={0}
            max={100}
            decimalScale={2}
            suffix="%"
          />
        </Group>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            color="wheels"
            loading={mutation.isPending}
            disabled={!itemId || !branchId || quantity <= 0}
            onClick={() => mutation.mutate()}
          >
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
